import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";

const TYPOLOGIES = [
  {
    id: "mega",
    label: "Mega Hub",
    img: "/assets/hub-mega.jpg",
    blurb: "Flagship metro & industrial hubs built for maximum flow and every freight task.",
    specs: [
      "100–150 secure long-bay truck parks",
      "Road-train assembly area (RTAA)",
      "Logistics storage, warehouse & cold storage",
      "Operations tower, maintenance & repair centre",
      "Fuel + EV charging (hydrogen-ready)",
      "Isolated dangerous-goods hardstand",
    ],
  },
  {
    id: "regional",
    label: "Regional Hub",
    img: "/assets/hub-regional.jpg",
    blurb: "Line-haul recovery hubs that keep long-distance corridors safe and moving.",
    specs: [
      "120+ secure truck capacity, well-lit & monitored",
      "6 service bays with tyre & wash",
      "Fuel, EV fast-charging, DEF & fluids",
      "Operations tower & driver lounge",
      "Modular pod village on site",
      "24/7 fenced, gated & CCTV-monitored",
    ],
  },
  {
    id: "pods",
    label: "Pod Village",
    img: "/assets/pod-interior.jpg",
    blurb: "The 'Better Rest' half of the brand: private, hotel-grade recovery for drivers.",
    specs: [
      "Private, climate-controlled sleep pods",
      "En-suite showers & on-site laundry",
      "Fresh-cooked meals & dining",
      "Fitness & outdoor recovery areas",
      "Keyless app access, 24/7 secure",
      "30–60 pods, scalable per corridor",
    ],
  },
];

export const HubTypologies = () => {
  const [id, setId] = useState("mega");
  const active = TYPOLOGIES.find((t) => t.id === id) || TYPOLOGIES[0];

  return (
    <section
      data-testid="hub-typologies-section"
      className="relative overflow-hidden py-20 lg:py-28"
      style={{ background: "linear-gradient(180deg,#F6F7FA 0%,#EEF1F6 100%)" }}
    >
      <div className="relative mx-auto w-full max-w-[1180px] px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-sm font-700 uppercase tracking-[0.18em] text-[#FF5722]">
            What&apos;s inside a hub
          </p>
          <h2 className="font-display text-3xl font-700 leading-[1.1] tracking-tight sm:text-5xl" style={{ color: NAVY }}>
            Not a truck stop.
            <span className="block text-[#FF5722]">A network of hubs.</span>
          </h2>
          <p className="mt-5 text-base font-400 leading-relaxed text-neutral-500">
            Purpose-built, truck-first facilities, engineered for safety, recovery and
            freight productivity, and connected by one app.
          </p>
        </div>

        {/* typology switcher */}
        <div
          data-testid="typology-toggle"
          className="mb-8 inline-flex flex-wrap gap-1 rounded-2xl bg-white p-1 ring-1 ring-[#FF5722]"
        >
          {TYPOLOGIES.map((t) => {
            const on = t.id === id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`typology-${t.id}`}
                onClick={() => setId(t.id)}
                className={`relative z-10 whitespace-nowrap rounded-xl px-5 py-2.5 text-[14px] font-600 transition-colors duration-300 ${
                  on ? "text-white" : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                {on && (
                  <motion.span
                    layoutId="typology-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className="absolute inset-0 -z-10 rounded-xl bg-[#FF5722] shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)]"
                  />
                )}
                <span className="relative">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* content */}
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.img}
              initial={{ opacity: 0, scale: 1.01 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              transition={{ duration: 0.55, ease: EASE }}
              className="overflow-hidden rounded-[24px] border border-black/5 bg-white shadow-[0_30px_60px_-30px_rgba(10,35,74,0.35)]"
            >
              <img src={active.img} alt={active.label} className="h-full w-full object-cover" draggable="false" />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.5, ease: EASE }}
              className="flex flex-col justify-center rounded-[24px] border border-black/5 bg-white p-7 shadow-[0_30px_60px_-30px_rgba(10,35,74,0.25)]"
            >
              <h3 className="font-display text-2xl font-700" style={{ color: NAVY }}>{active.label}</h3>
              <p className="mt-2 text-sm font-400 leading-relaxed text-neutral-500">{active.blurb}</p>
              <ul className="mt-5 space-y-3">
                {active.specs.map((s, i) => (
                  <motion.li
                    key={s}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.4, ease: EASE }}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#FF5722]/10">
                      <Check className="h-3 w-3 text-[#FF5722]" strokeWidth={3} />
                    </span>
                    <span className="text-[14px] font-500 text-neutral-700">{s}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
